import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Shield, Mail, Landmark, Bot, ArrowRight } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { useAuth } from '../../contexts/AuthContext';
import { api, errMsg } from '../../lib/api';

const STEPS = [
  { icon: Mail, title: 'Welcome to VEYJEX', body: 'Your account is created. This short onboarding explains how deposits, allocations, and settlements work.' },
  { icon: Shield, title: 'Secure your account', body: 'Use a strong, unique password. Passkeys and two-factor authentication can be enabled later in Security settings.' },
  { icon: Landmark, title: 'Deposit network', body: 'VEYJEX accepts verified USDT on the BNB Smart Chain (BEP20). Always confirm the network before sending funds.' },
  { icon: Bot, title: 'S.I. Bot & allocations', body: 'After a deposit is verified and credited, an eligible trading allocation can be assigned. The S.I. Bot runs in paper-trading mode until live execution is configured.' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const last = step === STEPS.length - 1;
  const S = STEPS[step];

  const finish = async () => {
    try {
      await api.put('/auth/profile', { onboarding_complete: true });
      updateUser({ onboarding_complete: true });
      toast.success('Onboarding complete');
      navigate('/dashboard/deposits');
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="min-h-screen bg-vj-obsidian text-vj-silver vj-grid-bg flex flex-col">
      <div className="p-5"><Logo size={34} /></div>
      <div className="flex-1 grid place-items-center px-4">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2 mb-8">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-vj-neon' : 'bg-white/10'}`} />
            ))}
          </div>
          <div className="vj-card p-8 text-center" data-testid="onboarding-card">
            <div className="w-16 h-16 rounded-2xl bg-vj-neon/10 grid place-items-center mx-auto mb-6">
              <S.icon size={30} className="text-vj-neon" />
            </div>
            <h1 className="font-display text-2xl font-bold text-foreground">{S.title}</h1>
            <p className="text-sm text-vj-grey mt-3">{S.body}</p>
            <div className="mt-8 flex gap-3">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="flex-1 h-11 rounded-lg border border-border text-sm" data-testid="onboarding-back">Back</button>
              )}
              <button
                onClick={last ? finish : () => setStep(step + 1)}
                data-testid="onboarding-next"
                className="flex-[2] h-11 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-all">
                {last ? <>Go to deposits <CheckCircle2 size={16} /></> : <>Continue <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
          <button onClick={finish} className="block mx-auto mt-5 text-xs text-vj-grey hover:text-vj-neon" data-testid="onboarding-skip">Skip onboarding</button>
        </div>
      </div>
    </div>
  );
}
